/**
 * platform/web/src/ui/state/library.ts
 *
 * The in-memory model of imported titles (Roadmap §4 screen 03 — Library / Home).
 *
 * A "title" is a game/homebrew the user has provided through the live load
 * pipeline: we record only its NAME, sniffed FORMAT, SIZE, and SOURCE (where it
 * came from — a dropped/picked file or onboarding). We deliberately do NOT keep
 * the bytes here; the model is metadata only, so it stays cheap, serialisable,
 * and — critically for §1.6 — it never holds (let alone decrypts) game content.
 *
 * Everything here is PURE and unit-tested under Node (test/library.test.mjs):
 *   - addTitle / dedupe (same name+size+format collapses to one entry, refreshed)
 *   - listTitles (insertion order, newest interaction first is the caller's job)
 *   - selectTitle / getSelected
 *   - removeTitle
 *
 * Persistence (IndexedDB / OPFS) is a LATER goal. For now the model talks to an
 * injectable {@link LibraryStorage} interface; an in-memory fake
 * ({@link createMemoryLibraryStorage}) is used in tests and as the default, so
 * the logic is verifiable today and a real adapter can slot in behind the same
 * seam without touching the model.
 */

import type { GameFormat } from "../../formats";

/** Where an imported title came from (used for the detail screen + provenance). */
export type TitleSource = "file" | "onboarding";

/**
 * The metadata of one imported title. `id` is a stable, content-derived key so
 * re-importing the same file collapses onto the same entry (see {@link titleId}).
 */
export interface Title {
  readonly id: string;
  readonly name: string;
  readonly format: GameFormat;
  /** Size in bytes of the provided image. */
  readonly size: number;
  readonly source: TitleSource;
  /** Epoch ms the title was first added. */
  readonly addedAt: number;
}

/** What a caller hands in to register a title; id/addedAt are derived. */
export interface TitleInput {
  readonly name: string;
  readonly format: GameFormat;
  readonly size: number;
  readonly source: TitleSource;
}

/** The whole library: an ordered list of titles + the current selection. */
export interface LibraryState {
  readonly titles: readonly Title[];
  readonly selectedId: string | null;
}

/**
 * The persistence seam. Synchronous + tiny on purpose: the in-memory fake is
 * trivial and a real adapter (localStorage now, IndexedDB later) can implement
 * the same shape. Persistence is a LATER goal; the model works without ever
 * calling these (they're optional wiring), but exposing them keeps the door open.
 */
export interface LibraryStorage {
  load(): LibraryState | null;
  save(state: LibraryState): void;
}

/** The empty library: no titles, nothing selected. */
export function emptyLibrary(): LibraryState {
  return { titles: [], selectedId: null };
}

/**
 * Stable id for a title derived from the fields that make it "the same game":
 * name + size + format. Re-importing the identical file therefore dedupes onto
 * one entry rather than piling duplicates into the grid. Pure; no hashing of
 * content (we never read game bytes here — §1.6), just its sniffed metadata.
 */
export function titleId(input: { name: string; size: number; format: GameFormat }): string {
  return `${input.format}:${input.size}:${input.name}`;
}

/**
 * Add (or refresh) a title. If a title with the same derived id already exists,
 * it is replaced IN PLACE (preserving its position and original addedAt) so a
 * re-import updates metadata like source without reordering or duplicating. The
 * newly-added/refreshed title becomes the selection. Never mutates the input.
 *
 * @param now epoch ms to stamp a brand-new entry with (injectable for tests).
 */
export function addTitle(state: LibraryState, input: TitleInput, now = Date.now()): LibraryState {
  const id = titleId(input);
  const existingIndex = state.titles.findIndex((t) => t.id === id);

  if (existingIndex >= 0) {
    const existing = state.titles[existingIndex];
    const refreshed: Title = {
      ...existing,
      name: input.name,
      format: input.format,
      size: input.size,
      source: input.source,
    };
    const titles = state.titles.slice();
    titles[existingIndex] = refreshed;
    return { titles, selectedId: id };
  }

  const title: Title = {
    id,
    name: input.name,
    format: input.format,
    size: input.size,
    source: input.source,
    addedAt: now,
  };
  return { titles: [...state.titles, title], selectedId: id };
}

/** The titles in insertion order (oldest first). */
export function listTitles(state: LibraryState): readonly Title[] {
  return state.titles;
}

/** Set the current selection. `null` clears it. Selecting an unknown id is a no-op. */
export function selectTitle(state: LibraryState, id: string | null): LibraryState {
  if (id !== null && !state.titles.some((t) => t.id === id)) return state;
  if (state.selectedId === id) return state;
  return { titles: state.titles, selectedId: id };
}

/** The currently-selected title, or null when none is selected / the id is stale. */
export function getSelected(state: LibraryState): Title | null {
  if (state.selectedId === null) return null;
  return state.titles.find((t) => t.id === state.selectedId) ?? null;
}

/** Look up a title by id (used by the detail screen). */
export function getTitle(state: LibraryState, id: string): Title | null {
  return state.titles.find((t) => t.id === id) ?? null;
}

/**
 * Remove a title by id. If it was the selection, the selection is cleared.
 * Removing an unknown id returns the same reference (no-op).
 */
export function removeTitle(state: LibraryState, id: string): LibraryState {
  if (!state.titles.some((t) => t.id === id)) return state;
  const titles = state.titles.filter((t) => t.id !== id);
  const selectedId = state.selectedId === id ? null : state.selectedId;
  return { titles, selectedId };
}

/**
 * A deep-ish clone of a {@link LibraryState} for the in-memory fake's save() so
 * callers can't mutate stored state through a shared reference. Titles are
 * shallow-immutable already, so cloning the array suffices.
 */
function cloneState(state: LibraryState): LibraryState {
  return { titles: state.titles.slice(), selectedId: state.selectedId };
}

/**
 * The in-memory storage fake: load() returns the last saved snapshot (or null
 * before anything is saved); save() takes a defensive copy. This is the default
 * adapter and the one tests drive, so the library logic is fully verifiable
 * without a browser. A real localStorage / IndexedDB adapter implements the same
 * {@link LibraryStorage} interface behind this seam.
 */
export function createMemoryLibraryStorage(seed?: LibraryState): LibraryStorage {
  let snapshot: LibraryState | null = seed ? cloneState(seed) : null;
  return {
    load(): LibraryState | null {
      return snapshot ? cloneState(snapshot) : null;
    },
    save(state: LibraryState): void {
      snapshot = cloneState(state);
    },
  };
}

/** A minimal localStorage-shaped surface, so the real adapter is typeable and
 *  testable without the DOM lib types and a fake can stand in for it. Mirrors the
 *  same shape settings.ts uses for its localStorage adapter. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The storage key the real adapter reads/writes under. Versioned so a future
 *  shape change can migrate rather than misread an old record. */
export const LIBRARY_STORAGE_KEY = "voland.library.v1";

/** The sniffed formats we accept back from a persisted record. An unknown value
 *  (corrupt/forward-version record) means we discard the whole snapshot rather
 *  than reconstruct a malformed {@link Title}. §1.6: this is metadata only. */
const VALID_FORMATS: ReadonlySet<string> = new Set<GameFormat>([
  "nro", "elf", "nso", "nca", "nsp", "xci", "unknown",
]);

/** The sources we accept back from a persisted record (see {@link TitleSource}). */
const VALID_SOURCES: ReadonlySet<string> = new Set<TitleSource>(["file", "onboarding"]);

/**
 * Reconstruct a {@link LibraryState} from a stored JSON string, or null when
 * there is nothing stored / the record is unusable. Every field is validated and
 * coerced so a corrupt or forward-version record degrades to "no library" (the
 * app starts empty) rather than crashing or producing malformed titles. Returns
 * null — not emptyLibrary() — so the caller can distinguish "nothing saved yet"
 * from "an explicitly empty library", matching {@link LibraryStorage.load}.
 */
function deserialize(raw: string | null): LibraryState | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (!Array.isArray(obj.titles)) return null;

  const titles: Title[] = [];
  for (const entry of obj.titles) {
    if (typeof entry !== "object" || entry === null) return null;
    const t = entry as Record<string, unknown>;
    if (
      typeof t.id !== "string" ||
      typeof t.name !== "string" ||
      typeof t.format !== "string" || !VALID_FORMATS.has(t.format) ||
      typeof t.size !== "number" || !Number.isFinite(t.size) ||
      typeof t.source !== "string" || !VALID_SOURCES.has(t.source) ||
      typeof t.addedAt !== "number" || !Number.isFinite(t.addedAt)
    ) {
      return null;
    }
    titles.push({
      id: t.id,
      name: t.name,
      format: t.format as GameFormat,
      size: t.size,
      source: t.source as TitleSource,
      addedAt: t.addedAt,
    });
  }

  // The selection must reference a present title; anything else clears it.
  const selectedId =
    typeof obj.selectedId === "string" && titles.some((x) => x.id === obj.selectedId)
      ? obj.selectedId
      : null;
  return { titles, selectedId };
}

/** Serialise a {@link LibraryState} to the stored JSON string. Metadata only —
 *  never any game bytes (§1.6). */
function serialize(state: LibraryState): string {
  return JSON.stringify({ titles: state.titles, selectedId: state.selectedId });
}

/**
 * The real persistence adapter over a {@link KeyValueStore} (Window.localStorage
 * in the browser), behind the SAME {@link LibraryStorage} seam the in-memory fake
 * implements. All access is wrapped so a throwing/blocked store (private mode,
 * quota) degrades gracefully — load() returns null (start empty) and save() drops
 * silently — rather than crashing the shell. §1.6: the library is metadata only
 * (name/format/size/source); no game bytes are ever stored here.
 */
export function createLocalStorageLibrary(
  store: KeyValueStore,
  key: string = LIBRARY_STORAGE_KEY,
): LibraryStorage {
  return {
    load(): LibraryState | null {
      try {
        return deserialize(store.getItem(key));
      } catch {
        return null;
      }
    },
    save(state: LibraryState): void {
      try {
        store.setItem(key, serialize(state));
      } catch {
        /* storage unavailable (private mode / quota) — drop silently. */
      }
    },
  };
}
